import EntityEditorMixin from 'vue-core/mixins/EntityEditorMixin';
import NumberUtilsMixin from 'vue-core/mixins/NumberUtilsMixin';
import FileUtilsMixin from 'vue-core/mixins/FileUtilsMixin';
import { downloadBlob } from 'vue-core/utils';
import * as moment from 'moment';
import { error2text } from 'core/utils';

export default {
  mixins: [ EntityEditorMixin, NumberUtilsMixin, FileUtilsMixin ],

  created() {
    // =========================================================================
    // Uploaded files columns
    // =========================================================================

    this.uploadedFilesPreColumns = [
      {
        label: '',
        style: 'max-width: 100px;',
        type: 'actions',
        actions: (item, index) => [
          {
            icon: 'times',
            tooltip: 'Elimina',
            buttonClass: 'text-danger pl-0',
            show: !this.disabled && this.value.permissions.removeFiles,
            exec: () => this.removeFile(item.filename)
          },
          {
            icon: 'download',
            tooltip: 'Scarica',
            buttonClass: 'text-primary pl-0',
            exec: () => this.download(item.filename)
          }
        ]
      },
    ];

    this.uploadedFilesPostColumns = [
      {
        label: 'Note',
        type: 'component',
        component: 'string-form-field',
        class: 'col-3 mb-0',
        props: (item, index) => ({
          multiline: true,
          disabled: this.disabled,
          value: item.notes,
          error: this.findError('uploadedFiles[' + index + '].notes')
        }),
        events: (item, index) => ({
          input: (v) => {
            const uploadedFiles = [ ...this.value.uploadedFiles ];
            uploadedFiles[index] = { ...item, notes: v };
            this.propertyChanged('uploadedFiles', uploadedFiles);
          }
        })
      },
    ];

    // =========================================================================
    // Extraction history columns
    // =========================================================================

    this.extractionHistoryColumns = [
      {
        label: '',
        style: 'max-width: 50px;',
        type: 'actions',
        show: !this.disabled,
        actions: (item, index) => [
          {
            icon: 'refresh',
            tooltip: 'Carica i dati da questa estrazione',
            buttonClass: 'text-primary pl-0',
            exec: () => this.goBackToExtraction(item)
          }
        ]
      },
      {
        label: 'Data e ora',
        type: 'timestamp',
        value: (item, index) => item.timestamp
      },
      {
        label: 'Utente',
        value: (item, index) => (item.userDisplayName || "<Non disponibile>")
      },
    ];

    // =========================================================================
    // Output history columns
    // =========================================================================

    this.outputHistoryColumns = [
      {
        label: '',
        style: 'max-width: 50px;',
        type: 'actions',
        show: !this.disabled,
        actions: (item, index) => [
          {
            icon: 'refresh',
            tooltip: 'Carica i dati da questa analisi',
            buttonClass: 'text-primary pl-0',
            exec: () => this.goBackToAnalysis(item)
          }
        ]
      },
      {
        label: 'Data e ora',
        type: 'timestamp',
        value: (item, index) => item.timestamp
      },
      {
        label: 'Utente',
        value: (item, index) => (item.userDisplayName || "<Non disponibile>")
      },
    ];
  },

  data() {
    return {
      pendingFiles: [],
      loading: false,
      tabIndex: 0,
      dirtyInput: false,
    };
  },

  watch: {
    loading(newValue) {
      if (newValue) {
        this.$refs.loadingModal.show();
      } else {
        this.$refs.loadingModal.hide();
      }
    },
  },

  methods: {
    // =========================================================================
    // File upload/download
    // =========================================================================

    setProgress(file, loaded, total) {
      this.$refs.fileTable.setProgress(file, loaded, total);
    },

    setFileError(file, error) {
      this.$refs.fileTable.setError(file, error);
    },

    upload(files) {
      const promises = [];
      const uploaded = {};
      for (let f of files) {
        const progress = (loaded, total) => {
          this.setProgress(f, loaded, total);
        }

        const success = ({ statusCode, data }) => {
          this.setProgress(f, f.size, f.size);
          uploaded[f.id] = data;
          return Promise.resolve(true);
        }

        const error = ({ statusCode, data }) => {
          this.setFileError(f, data);
          return Promise.reject(false);
        }

        this.loading = true;
        const prom = this.service.uploadFile({
          analysisId: this.value.analysis.id,
          tempId: this.value.tempId,
          file: f,
          progress
        }).then(success, error);

        promises.push(prom);
      }

      Promise.all(promises).then(() => {
        const uploadedFiles = [ ...this.value.uploadedFiles ];
        for (let k of Object.keys(uploaded)) {
          uploadedFiles.push(uploaded[k]);
        }
        this.propertyChanged('uploadedFiles', uploadedFiles);
        this.loading = false;
      }, () => {
        const uploadedFiles = [ ...this.value.uploadedFiles ];
        for (let k of Object.keys(uploaded)) {
          uploadedFiles.push(uploaded[k]);
        }
        this.propertyChanged('uploadedFiles', uploadedFiles);
        this.loading = false;
      });
    },

    download(filename) {
      const success = ({ statusCode, data }) => {
        console.log('Download success:', statusCode, data);
        downloadBlob(data, filename);
        //this.loading = false;
      }

      const error = ({ statusCode, data }) => {
        console.log('Download error:', statusCode, data);
        alert(error2text(data));
        //this.loading = false;
      }

      //this.loading = true;
      this.service.download({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        filename
      }).then(success, error);

      return false;
    },

    removeFile(filename) {
      if (!confirm('Sei sicuro di voler eliminare il file?\nL\'operazione non è reversibile.')) {
        return false;
      }

      const success = ({ statusCode, data }) => {
        console.log('Remove success:', statusCode, data);
        const uploadedFiles = this.value.uploadedFiles.filter(f => f.filename !== filename);
        this.propertyChanged('uploadedFiles', uploadedFiles);
        this.loading = false;
      }

      const error = ({ statusCode, data }) => {
        console.log('Remove error:', statusCode, data);
        alert(error2text(data));
        this.loading = false;
      }

      this.loading = true;
      this.service.removeFile({
        analysisId: this.value.analysis.id,
        id: this.value.id,
        tempId: this.value.tempId,
        filename
      }).then(success, error);

      return false;
    },

    // =========================================================================
    // Extraction
    // =========================================================================

    onExtractDataSuccess({ statusCode, data }) {
      log.error('Virtual method');
      throw Error('Virtual method');
    },

    extractData(forceProcessing) {
      const error = ({ statusCode, data }) => {
        console.log('Extraction error:', statusCode, data);
        this.loading = false;
        alert(error2text(data));
      }

      this.loading = true;
      this.service.extractData({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        files: this.value.uploadedFiles.map(item => item.filename),
        forceProcessing
      }).then(this.onExtractDataSuccess.bind(this), error);

      return false;
    },

    goBackToExtraction(item) {
      // console.log('Going back to extraction');

      const msg = "Sei sicuro di voler ricaricare i dati di questa estrazione?\n"
          + "L'operazione cancellerà i dati di input correnti e sarà "
          + "necessario eseguire nuovamente l'analisi.";

      if (!confirm(msg)) {
        return false;
      }

      const success = ({ statusCode, data }) => {
        //this.takeCareOfNaNs(data);
        console.log('Extraction success:', statusCode, data);
        this.loading = false;
        this.dirtyInput = true;
        //this.$refs.extractionAdvice.show();

        const obj = { ...this.value };
        if (data.input) {
          obj.input = data.input;
        }
        if (data.extractionHistory) {
          obj.extractionHistory = data.extractionHistory;
        }
        this.$emit('input', obj);

        this.tabIndex = 1;
      }

      const error = ({ statusCode, data }) => {
        console.log('Extraction error:', statusCode, data);
        this.loading = false;
        alert(error2text(data));
      }

      this.loading = true;
      this.service.goBackToExtraction({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        source: item.source
      }).then(success, error);

      return false;
    },

    exportData(exportType, forceProcessing) {
      const success = ({ statusCode, data }) => {
        console.log('Export success:', statusCode, data);
        downloadBlob(data, 'data.' + exportType);
        this.loading = false;
      }

      const error = ({ statusCode, data }) => {
        console.log('Export error:', statusCode, data);
        alert(error2text(data));
        this.loading = false;
      }

      this.loading = true;
      this.service.exportData({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        files: this.value.uploadedFiles.map(item => item.filename),
        type: exportType,
        forceProcessing
      }).then(success, error);

      return false;
    },

    // =========================================================================
    // Analysis
    // =========================================================================

    onAnalyzeSuccess({ statusCode, data }) {
      log.error('Virtual method');
      throw Error('Virtual method');
    },

    analyze(analysisTypeId, forceProcessing) {
      const error = ({ statusCode, data }) => {
        console.log('Analysis error:', statusCode, data);
        this.loading = false;
        alert(error2text(data));
      }

      this.loading = true;
      this.service.analyze({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        analysisTypeId,
        input: this.value.input,
        forceProcessing
      }).then(this.onAnalyzeSuccess.bind(this), error);
    },

    onGoBackToAnalysisSuccess({ statusCode, data }) {
      log.error('Virtual method');
      throw Error('Virtual method');
    },

    goBackToAnalysis(item) {
      // console.log('Going back to analysis');

      const msg = "Sei sicuro di voler ricaricare i dati di questa analisi?\n"
          + "L'operazione cancellerà i dati di bilancio correnti e qualunque "
          + " analisi già eseguita, sovrascrivendo i valori con quelli "
          + " dell'analisi che si sta richiamando";

      if (!confirm(msg)) {
        return false;
      }

      const error = ({ statusCode, data }) => {
        console.log('Extraction error:', statusCode, data);
        this.loading = false;
        alert(error2text(data));
      }

      this.loading = true;
      this.service.goBackToAnalysis({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        source: item.source
      }).then(this.onGoBackToAnalysisSuccess.bind(this), error);

      return false;
    },

    exportAnalysis(analysisTypeId, exportType) {
      const success = ({ statusCode, data }) => {
        console.log('Analysis success:', statusCode, data);
        downloadBlob(data, 'export.' + exportType);
        this.loading = false;
      }

      const error = ({ statusCode, data }) => {
        console.log('Analysis error:', statusCode, data);
        alert(error2text(data));
        this.loading = false;
      }

      this.loading = true;
      this.service.exportAnalysis({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        analysisTypeId,
        input: this.value.input,
        type: exportType,
        includeInput: true
      }).then(success, error);
    },
  }
};
